import { useDisclosure } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  MagnifyingGlassPurpleGuy,
  OdometerPurpleGuy,
  OkGestureGuy,
  PointingGuy,
} from '../../assets/Images';
import { LEAccordion, LEAccordionItem } from '../../components/LEAccordion';
import LEAccordionHeader from '../../components/LEAccordion/LEAccordionHeader';
import LEAccordionContainer from '../../components/LEAccordion/LeAccordionContainer';
import { ACCORDION_NAME } from '../../components/LEAccordion/utils';
import Loader from '../../components/Loader';
import BackButton from '../../components/ui/buttons/BackButton';
import { ButtonWrapper } from '../../components/ui/buttons/ButtonWrapper';
import { LDFlags } from '../../constants/flags';
import { AFFILIATE_ENTRY, MKT_HOME } from '../../constants/urls';
import { useAddressDetailQuery, useTemporaryInfoUpdateMutation } from '../../gql/generated/graphql';
import { useFlag, useStep, useTempInfo } from '../../hooks';
import { CookieKeys, useCookie } from '../../hooks/useCookie';
import useIsSellingAvailable from '../../hooks/useIsSellingAvailable';
import { resetAuth } from '../../utils/auth';
import { isStateEligibleForMethodFi } from '../../utils/methodFi';
import { AFFILIATE_PARAMS, isSellerEntry } from '../flowEntryPages/utils';
import JustTheBasics from './components/JustTheBasicsItem/Content';
import KeepMyCarPanel from './components/KeepMyCarPanel';
import KeepMyCarTitle from './components/KeepMyCarTitle';
import LooksRightItem from './components/LooksRightItem';
import MethodFiDevToolbar from './components/MethodFi/MethodFiDevToolbar';
import MethodFiEntry from './components/MethodFi/MethodFiEntry';
import PersonalInfo from './components/PersonalInfo';
import WhoIsYourLease from './components/WhoIsYourLease';

export type DevPayoffValues = {
  netPayoff: number | null;
  salesTax: number | null;
};

const GettingStarted: React.FC = () => {
  const history = useHistory();
  const { info, infoLoading, refetch } = useTempInfo();
  const { onToggle: refreshToggle } = useDisclosure();
  const step = useStep();

  const [accessToken] = useCookie<string>(CookieKeys.ACCESS_TOKEN);
  const isAuthenticated = !!accessToken;

  const keepMyCarSelectorEnabled = useIsSellingAvailable(info?.data);
  const hideBackButton = useFlag(LDFlags.HIDE_GETTING_STARTED_BACK_BUTTON);

  const isMethodFiEntry = info?.data?.is_methodfi_flow ?? false;

  const { data: addressData, loading: addressLoading } = useAddressDetailQuery({
    variables: { zipCode: info?.data?.zip },
    skip: !isMethodFiEntry || !info?.data?.zip,
  });

  const isMethodFiEligible =
    !!isMethodFiEntry && isStateEligibleForMethodFi(addressData?.addressDetail?.state);

  const [showingManualPanel, setShowingManualPanel] = useState(false);
  const [showingVehicleSelect, setShowingVehicleSelect] = useState(false);
  const [unsupportedSellError, setUnsupportedSellError] = useState<string | null>(null);
  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const [buyoutLoading, setBuyoutLoading] = useState(false);

  const onBuyout = async () => {
    setBuyoutLoading(true);
    try {
      await updateTemporaryInfo({
        variables: { info: { id: info?.id, data: { wants_to_sell: false } } },
      });
      setUnsupportedSellError(null);
      await refetch();
    } finally {
      setBuyoutLoading(false);
    }
  };

  const sellOnlyPanel =
    keepMyCarSelectorEnabled &&
    isSellerEntry(info?.data?.query_params) &&
    info?.data?.wants_to_sell === true;

  useEffect(() => {
    if (!infoLoading && info?.data && !info.data.vin && !isMethodFiEntry) {
      if (info.data.affiliate_external_id) {
        history.replace(
          `${AFFILIATE_ENTRY}?${AFFILIATE_PARAMS.AFFILIATE_EXTERNAL_ID}=${info.data.affiliate_external_id}`,
        );
      } else {
        resetAuth({ removeGuid: true });
        history.replace(MKT_HOME);
      }
    }
  }, [info, infoLoading]);

  const isLoading = (infoLoading && !info) || (isMethodFiEntry && addressLoading);

  if (isLoading) {
    return <Loader />;
  }

  const getFirstPanelTitle = () => {
    if (!isMethodFiEntry) return 'DOES THIS LOOK RIGHT?';
    if (showingVehicleSelect) return 'IS THIS YOUR LEASED VEHICLE?';
    if (showingManualPanel) return 'FIND YOUR CAR';
    return isMethodFiEligible ? 'VERIFY YOUR PHONE NUMBER' : 'FIND YOUR CAR';
  };

  const keepMyCarTitle = sellOnlyPanel ? 'SELL MY CAR' : <KeepMyCarTitle />;

  const getPageTitle = () => {
    if (!isMethodFiEntry) return 'We found your car!';
    return isMethodFiEligible ? 'Getting Started!' : "Let's get started";
  };

  return (
    <LEAccordionContainer>
      <LEAccordionHeader>{getPageTitle()}</LEAccordionHeader>
      <LEAccordion name={ACCORDION_NAME.VEHICLE_ACCORDION} minutes={1}>
        {isMethodFiEntry ? (
          <LEAccordionItem
            accordionKey="methodFiEntry"
            title={getFirstPanelTitle()}
            imgSrc={MagnifyingGlassPurpleGuy}
          >
            <MethodFiEntry
              isEligible={isMethodFiEligible}
              onShowingManualPanel={setShowingManualPanel}
              onVehicleSelectMode={setShowingVehicleSelect}
            />
          </LEAccordionItem>
        ) : (
          <LEAccordionItem
            accordionKey="vehicle"
            title="DOES THIS LOOK RIGHT?"
            imgSrc={MagnifyingGlassPurpleGuy}
          >
            <LooksRightItem />
          </LEAccordionItem>
        )}
        <LEAccordionItem
          accordionKey="odoZipColor"
          title="MILEAGE & COLOR"
          imgSrc={OdometerPurpleGuy}
        >
          <JustTheBasics refreshToggle={refreshToggle} />
        </LEAccordionItem>
        <LEAccordionItem
          accordionKey="lienholder"
          title="WHO IS YOUR LEASE THROUGH?"
          imgSrc={PointingGuy}
          contentWrapperProps={{ zIndex: 1 }}
        >
          <WhoIsYourLease
            clearUnsupportedSellError={() => setUnsupportedSellError(null)}
            onBuyout={onBuyout}
          />
        </LEAccordionItem>
        {!isAuthenticated && (
          <LEAccordionItem
            accordionKey="personalInfo"
            title={keepMyCarSelectorEnabled ? keepMyCarTitle : 'STAY INFORMED ON YOUR OFFER'}
            imgSrc={OkGestureGuy}
          >
            <PersonalInfo
              unsupportedSellError={unsupportedSellError}
              setUnsupportedSellError={setUnsupportedSellError}
              keepMyCarSelectorEnabled={keepMyCarSelectorEnabled}
              sellOnlyPanel={sellOnlyPanel}
              onBuyout={onBuyout}
              buyoutLoading={buyoutLoading}
            />
          </LEAccordionItem>
        )}
        {isAuthenticated && keepMyCarSelectorEnabled && (
          <LEAccordionItem accordionKey="keepMyCar" title={keepMyCarTitle} imgSrc={OkGestureGuy}>
            <KeepMyCarPanel
              unsupportedSellError={unsupportedSellError}
              setUnsupportedSellError={setUnsupportedSellError}
              sellOnlyPanel={sellOnlyPanel}
              onBuyout={onBuyout}
              buyoutLoading={buyoutLoading}
            />
          </LEAccordionItem>
        )}
      </LEAccordion>
      {!hideBackButton && (
        <ButtonWrapper>
          <BackButton zIndex={0} onClick={() => step.movePrevious({ redirect: true })} />
        </ButtonWrapper>
      )}
      {isMethodFiEntry && <MethodFiDevToolbar />}
    </LEAccordionContainer>
  );
};

export default GettingStarted;
